import { Routes, Route, useLocation } from 'react-router-dom';
import React, { Suspense, lazy, useEffect } from 'react';

// Layouts
import MainLayout from './../components/layout/MainLayout';
import ConvertData from './../pages/frontend/ConvertData';
import { getConverterRoutes } from './convertRoute';
import { getFormattorRoutes } from './formatterRoute';
import { getToolsRoutes } from './toolsRoute';
import LoadingSpinner from './../components/LoadingSpinner';
import { initGA, trackPageView } from './../utils/GoogleAnalytics';
import FormatterData from './../pages/frontend/Formatter';
import ContactForm from './../pages/frontend/Contact';
import Blog from './../pages/frontend/Blog';
import Sitemap from './../pages/frontend/Sitemap';
import TermsOfService from './../pages/frontend/TermsOfService';
import PrivacyPolicy from './../pages/frontend/PrivacyPolicy';
import AboutUs from './../pages/frontend/AboutUs';
import Newsletter from './../pages/frontend/Newsletter';
import Feedback from './../pages/frontend/Feedback';
import Logs from './../pages/frontend/logsData';
import ToolsData from './../pages/frontend/Tools';

// Pages - Lazy loaded
const Home = lazy(() => import('./../pages/frontend/Home'));
const NotFound = lazy(() => import('./../pages/common/NotFound'));

// Loading component
const Loading = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const AppRoutes = () => {
  useEffect(() => {
    initGA();
  }, []);

  const converterRoutes = getConverterRoutes();
  const toolsRoutes = getToolsRoutes(); // Get tools routes
  const formatterRoutes = getFormattorRoutes(); // Get formatter routes

  const LazyConvertData = lazy(() => import('./../pages/frontend/ConvertData'));
  const LazyFormatterData = lazy(() => import('./../pages/frontend/Formatter'));
  const LazyToolsData = lazy(() => import('./../pages/frontend/Tools'));

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<Home />} />
          <Route path="/contact" element={<ContactForm />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/newsletter" element={<Newsletter />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/logsData" element={<Logs />} />

          {/* Base routes */}
          <Route path="/converter" element={<ConvertData />} />
          <Route path="/formatter" element={<FormatterData />} />
          <Route path="/tools" element={<ToolsData />} />

          {/* Category level routes */}
          <Route path="/formatter/:categoryId" element={
            <Suspense fallback={<LoadingSpinner />}>
              <LazyFormatterData />
            </Suspense>
          } />

          <Route path="/converter/:categoryId" element={
            <Suspense fallback={<LoadingSpinner />}>
              <LazyConvertData />
            </Suspense>
          } />

          {/* Tools Routes - Generated from routes.ts */}
          {toolsRoutes?.map((route: any, index: any) => {
            const LazyComponent = route.element;
            return (
              <Route
                key={`tools-${index}`}
                path={route.path}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyComponent />
                  </Suspense>
                }
              />
            );
          })}

          {/* Formatter Routes - Generated from routes.ts */}
          {formatterRoutes?.map((route: any, index: any) => {
            const LazyComponent = route.element;
            return (
              <Route
                key={`formatter-${index}`}
                path={route.path}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyComponent />
                  </Suspense>
                }
              />
            );
          })}

          {/* Converter Routes - Dynamic from convertRoute */}
          {converterRoutes?.map((route, index) => {
            const LazyComponent = route.element;
            return (
              <Route
                key={`converter-${index}`}
                path={route.path}
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyComponent />
                  </Suspense>
                }
              />
            );
          })}

          {/* 404 - Not Found - Must be last */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;